import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import PromptBuilder from '../components/PromptBuilder';
import {
  ArrowUpTrayIcon,
  DocumentIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  XMarkIcon
} from '@heroicons/react/24/solid';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function ExploradorArchivos() {
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.0);
  const [selectedLines, setSelectedLines] = useState([]);
  const [processedLines, setProcessedLines] = useState({});
  const highlightRefs = useRef({});

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setPdfFile(file);
      const url = URL.createObjectURL(file);
      setPdfUrl(url);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false
  });

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3.0));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleResetZoom = () => {
    setScale(1.0);
  };

  const handleClearFile = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
    setPdfFile(null);
    setPdfUrl(null);
    setNumPages(null);
    setScale(1.0);
    setSelectedLines([]);
    setProcessedLines({});
  };

  const handleHighlightLines = (sourceIds) => {
    setSelectedLines(sourceIds);
  };

  // Auto-scroll to highlighted line
  useEffect(() => {
    if (selectedLines.length > 0 && highlightRefs.current[selectedLines[0]]) {
      highlightRefs.current[selectedLines[0]].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [selectedLines]);

  const handleProcessedData = (data) => {
    // Store the lines data for highlighting
    setProcessedLines(data.lines || {});
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-6 pb-4 border-b">
        <div className="flex justify-between items-center">
          <div>
            <Heading level={1} className="text-3xl font-bold text-azul-surelia mb-2">
              Explorador de Archivos
            </Heading>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden bg-gray-50">
        {/* PDF Viewer - 3/5 width */}
        <div className="w-3/5 flex flex-col bg-white border-r">
          {!pdfFile ? (
            // Dropzone
            <div className="flex-1 flex items-center justify-center p-8">
              <div
                {...getRootProps()}
                className={`w-full max-w-2xl border-2 border-dashed rounded-xl p-16 text-center cursor-pointer transition-all ${
                  isDragActive
                    ? 'border-azul-surelia bg-azul-surelia/5'
                    : 'border-gray-300 hover:border-azul-surelia'
                }`}
              >
                <input {...getInputProps()} />
                <div className={`w-20 h-20 mx-auto mb-6 rounded-xl flex items-center justify-center ${
                  isDragActive
                    ? 'bg-azul-surelia'
                    : 'bg-azul-surelia/10'
                }`}>
                  <ArrowUpTrayIcon className={`h-10 w-10 ${
                    isDragActive ? 'text-white' : 'text-azul-surelia'
                  }`} />
                </div>
                {isDragActive ? (
                  <Text className="text-lg text-azul-surelia font-semibold">
                    Suelta el archivo aquí
                  </Text>
                ) : (
                  <>
                    <Text className="text-lg text-gray-900 font-semibold mb-2">
                      Arrastra un archivo PDF aquí
                    </Text>
                    <Text className="text-sm text-gray-500">
                      o haz clic para seleccionar un archivo
                    </Text>
                  </>
                )}
              </div>
            </div>
          ) : (
            // PDF Viewer
            <>
              {/* PDF Controls */}
              <div className="flex items-center justify-between px-4 py-3 bg-white border-b">
                <div className="flex items-center gap-2">
                  <DocumentIcon className="h-5 w-5 text-azul-surelia" />
                  <Text className="font-medium text-sm truncate max-w-xs text-gray-900">
                    {pdfFile.name}
                  </Text>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleZoomOut}
                      className="p-1 hover:bg-gray-200 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Alejar"
                      disabled={scale <= 0.5}
                    >
                      <MagnifyingGlassMinusIcon className="h-5 w-5 text-gray-700" />
                    </button>
                    <button
                      onClick={handleResetZoom}
                      className="px-2 py-1 hover:bg-gray-200 rounded transition-colors text-sm font-medium text-gray-700"
                      title="Restablecer zoom"
                    >
                      {Math.round(scale * 100)}%
                    </button>
                    <button
                      onClick={handleZoomIn}
                      className="p-1 hover:bg-gray-200 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Acercar"
                      disabled={scale >= 3.0}
                    >
                      <MagnifyingGlassPlusIcon className="h-5 w-5 text-gray-700" />
                    </button>
                  </div>
                  <button
                    onClick={handleClearFile}
                    className="p-1 hover:bg-red-100 rounded transition-colors"
                    title="Cerrar archivo"
                  >
                    <XMarkIcon className="h-5 w-5 text-red-600" />
                  </button>
                </div>
              </div>

              {/* PDF Display */}
              <div className="flex-1 overflow-auto p-4 bg-gray-50">
                <div className="mx-auto" style={{ width: 'fit-content' }}>
                  {pdfUrl && (
                    <Document
                      file={pdfUrl}
                      onLoadSuccess={onDocumentLoadSuccess}
                      className="flex flex-col"
                    >
                      {Array.from(new Array(numPages), (_, index) => {
                        const pageNumber = index + 1;
                        // Get highlights for this page
                        const highlights = selectedLines
                          .filter(lineId => processedLines[lineId]?.page === pageNumber)
                          .map(lineId => [lineId, processedLines[lineId]]);

                        return (
                          <div key={pageNumber} className="mb-4 relative" style={{ display: 'inline-block' }}>
                            <Page pageNumber={pageNumber} scale={scale} />
                            {/* Render highlight boxes */}
                            {highlights.map(([id, line]) => {
                              const padding = 0.005;
                              return (
                                <div
                                  key={id}
                                  ref={(el) => {
                                    if (el) highlightRefs.current[id] = el;
                                  }}
                                  style={{
                                    position: 'absolute',
                                    left: `${Math.max(0, line.boundingBox.left - padding) * 100}%`,
                                    top: `${Math.max(0, line.boundingBox.top - padding) * 100}%`,
                                    width: `${Math.min(1, line.boundingBox.width + 2 * padding) * 100}%`,
                                    height: `${Math.min(1, line.boundingBox.height + 2 * padding) * 100}%`,
                                    border: '2px solid #340895',
                                    backgroundColor: 'rgba(52, 8, 149, 0.1)',
                                    pointerEvents: 'none',
                                    boxSizing: 'border-box'
                                  }}
                                />
                              );
                            })}
                          </div>
                        );
                      })}
                    </Document>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right Panel - 2/5 width */}
        <div className="w-2/5 bg-gray-50  overflow-y-auto">
          <PromptBuilder
            pdfFile={pdfFile}
            pdfUrl={pdfUrl}
            onHighlightLines={handleHighlightLines}
            onProcessedData={handleProcessedData}
          />
        </div>
      </div>
    </div>
  );
}
